import React, { useRef, useState, useEffect, CSSProperties } from 'react'

interface FileUploaderProps {
  /** Array of allowed file extensions without the dot, e.g. ['jpg', 'png', 'pdf'] */
  acceptedTypes: string[]
  /** Maximum file size in megabytes */
  maxFileSizeMB: number
  /** Callback when a valid file is selected */
  onFileSelect: (file: File | null) => void
  /** Optional initial file to preview */
  initialFile?: File
  /** Optional label to display above the uploader */
  label?: string
  /** Optional description to display below the label */
  description?: string
  /** Optional custom class for the container */
  className?: string
}

const defaultStyles: { [key: string]: CSSProperties } = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
  },
  label: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#333',
    marginBottom: '0.5rem',
    textAlign: 'left',
  },
  description: {
    fontSize: '0.875rem',
    color: '#555',
    marginBottom: '0.5rem',
    textAlign: 'left',
  },
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    backgroundColor: '#f9f9f9',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '150px',
  },
  hover: {
    backgroundColor: '#f0f0f0',
  },
  icon: {
    width: '40px',
    height: '40px',
    marginBottom: '0.5rem',
    fill: '#888',
  },
  text: {
    fontSize: '0.9rem',
    color: '#555',
    margin: 0,
    textAlign: 'center',
  },
  info: {
    fontSize: '0.75rem',
    color: '#777',
    marginTop: '0.25rem',
    textAlign: 'center',
  },
  error: {
    color: 'red',
    marginTop: '0.5rem',
    fontSize: '0.875rem',
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  previewContent: {
    width: '100%',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '4px',
  },
  previewPDF: {
    width: '100%',
    height: '200px',
    border: 'none',
    borderRadius: '4px',
  },
  fileName: {
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    color: '#333',
  },
  buttonGroup: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    gap: '0.5rem',
  },
  button: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  downloadButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
  },
  removeButton: {
    backgroundColor: '#f44336',
    color: 'white',
  },
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  acceptedTypes,
  maxFileSizeMB,
  onFileSelect,
  initialFile,
  label,
  description,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [hover, setHover] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(initialFile || null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(selectedFile)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [selectedFile])

  const handleFileClick = () => {
    setError(null)
    inputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const allowed = acceptedTypes.map(t => t.toLowerCase())
    if (!allowed.includes(ext)) {
      setError(`Tipo no permitido. Solo: ${acceptedTypes.join(', ')}`)
      return
    }
    const maxBytes = maxFileSizeMB * 1024 * 1024
    if (file.size > maxBytes) {
      setError(`Tamaño máximo: ${maxFileSizeMB} MB.`)
      return
    }
    setError(null)
    setSelectedFile(file)
    onFileSelect(file)
  }

  const handleRemove = () => {
    setSelectedFile(null)
    setError(null)
    onFileSelect(null)
  }

  const renderPreviewContent = () => {
    if (!selectedFile || !previewUrl) return null
    const ext = selectedFile.name.split('.').pop()?.toLowerCase() || ''
    if (ext === 'pdf') {
      return (
        <object
          data={previewUrl}
          type="application/pdf"
          style={defaultStyles.previewPDF}
        />
      )
    }
    return (
      <img src={previewUrl} alt={selectedFile.name} style={defaultStyles.previewImage} />
    )
  }

  const renderContainerContent = () => {
    if (!selectedFile) {
      return (
        <>
          <svg
            viewBox="0 0 24 24"
            style={defaultStyles.icon}
            xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM13 3.5L18.5 9H13V3.5z" />
          </svg>
          <p style={defaultStyles.text}>Haz clic o arrastra para subir archivo</p>
          <p style={defaultStyles.info}>
            Solo: {acceptedTypes.join(', ')} · Máx. {maxFileSizeMB} MB
          </p>
        </>
      )
    }
    return (
      <div style={defaultStyles.preview}>
        <div style={defaultStyles.previewContent}>{renderPreviewContent()}</div>
        <div style={defaultStyles.fileName}>{selectedFile.name}</div>
      </div>
    )
  }

  return (
    <div className={className} style={defaultStyles.wrapper}>
      {label && <div style={defaultStyles.label}>{label}</div>}
      {description && <div style={defaultStyles.description}>{description}</div>}
      <div
        style={{ ...defaultStyles.container, ...(hover ? defaultStyles.hover : {}) }}
        onClick={handleFileClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}>
        {renderContainerContent()}
        <input
          type="file"
          ref={inputRef}
          style={{ display: 'none' }}
          accept={acceptedTypes.map(t => `.${t}`).join(',')}
          onChange={handleFileChange}
        />
        {selectedFile && (
          <div style={defaultStyles.buttonGroup} onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={handleRemove}
              style={{ ...defaultStyles.button, ...defaultStyles.removeButton }}>
              Descartar
            </button>
          </div>
        )}
        {error && <div style={defaultStyles.error}>{error}</div>}
      </div>
    </div>
  )
}
